import { RefObject, SyntheticEvent, useContext, useEffect, useMemo } from 'react'
import { useNodeRef } from '../../../../hooks/use-node-ref'
import { Coordinates } from '../../../../types'
import { PortalTooltipContext } from '../portal-tooltip/context'
import { TooltipDirection } from '../types'

export type TooltipProps = {
    color?: string
    dimmed?: boolean
    direction?: TooltipDirection
    id: string
    offset?: Partial<Coordinates>
    width?: number
}

type TooltipHookProps = {
    isOpen: boolean
    listeners: Record<string, Function>
    node: RefObject<HTMLElement>
    setNodeRef: (element: HTMLElement | null) => void
}

const useTooltip = ({ color, dimmed, direction, id, offset, width }: TooltipProps): TooltipHookProps => {
    const { activeId, tooltips, openTooltip } = useContext(PortalTooltipContext)
    const [node, setNodeRef] = useNodeRef()

    const listeners = useMemo(
        () => ({
            onClick: (e: SyntheticEvent<HTMLElement, Event>) => {
                e.preventDefault()
                openTooltip(node.current || e.currentTarget, id)
            },
        }),
        [id, node, openTooltip]
    )

    useEffect(() => {
        if (!tooltips) return

        tooltips.set(id, {
            color,
            dimmed,
            direction,
            id,
            offset,
            width,
        })

        return () => {
            const data = tooltips.get(id)

            if (data) {
                tooltips.delete(id)
            }
        }
    }, [color, dimmed, direction, id, offset?.x, offset?.y, tooltips])

    return {
        isOpen: id === activeId,
        listeners,
        node,
        setNodeRef,
    }
}

export default useTooltip
